import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  countDepartmentEmployees,
  createDepartment,
  deleteDepartment,
  getAllOrganizationDepartments,
  getDepartments,
  updateDepartment,
} from '../services/department.service';
import { createDepartmentRole, syncDepartmentRoles } from '../services/department-role.service';
import {
  Department,
  DepartmentInsertInput,
  DepartmentUpdateInput,
  GetDepartmentsParams,
} from '../types/department.types';

const invalidateDepartmentQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-departments'] });

export const useGetDepartments = (params: GetDepartmentsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-departments', params],
    queryFn: () => getDepartments(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: async ({
      input,
      organizationName,
    }: {
      input: DepartmentInsertInput;
      organizationName: string;
    }) => {
      const result = await createDepartment(input);
      // Its Head of Department role (slug = Key). The department record is the source of truth and
      // must not be rolled back for this — a failure here is surfaced separately ("Sync roles" can
      // create the role later) rather than failing the whole create.
      try {
        await createDepartmentRole({ key: input.Key, title: input.Title, organizationName });
      } catch (error) {
        console.error('Failed to create the department role', error);
        toast({
          variant: 'destructive',
          title: t(
            (error as Error).message === 'ROLE_KEY_ARCHIVED'
              ? 'DEPARTMENT_ROLE_KEY_ARCHIVED'
              : 'DEPARTMENT_ROLE_CREATE_FAILED'
          ),
        });
      }
      return result;
    },
    onSuccess: () => {
      invalidateDepartmentQueries(queryClient);
      toast({ title: t('DEPARTMENT_CREATED') });
    },
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: DepartmentUpdateInput }) =>
      updateDepartment(params),
    onSuccess: () => {
      invalidateDepartmentQueries(queryClient);
      toast({ title: t('DEPARTMENT_UPDATED') });
    },
  });
};

/** Creates the missing Head of Department roles of the organization's departments. */
export const useSyncDepartmentRoles = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: async (organizationName: string) =>
      syncDepartmentRoles(await getAllOrganizationDepartments(), organizationName),
    onSuccess: ({ existing, created, failed }) => {
      if (failed) {
        toast({
          variant: 'destructive',
          title: t('DEPARTMENT_ROLES_SYNC_FAILED', { failed, created, existing }),
        });
        return;
      }
      toast({ title: t('DEPARTMENT_ROLES_SYNCED', { created, existing }) });
    },
  });
};

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: async (department: Pick<Department, 'ItemId' | 'Key'>) => {
      // A department people still belong to can't be deleted — they'd be left pointing at a
      // department that no longer exists. They have to be moved to another department first.
      if ((await countDepartmentEmployees(department.ItemId)) > 0) {
        toast({ variant: 'destructive', title: t('DEPARTMENT_HAS_EMPLOYEES') });
        return { deleted: false };
      }
      // A soft delete: the department's Head of Department role stays, with whoever holds it, so its
      // records stay reachable and re-creating the department reuses the role. IAM can't restore a
      // deleted role.
      await deleteDepartment(department.ItemId);
      return { deleted: true };
    },
    onSuccess: ({ deleted }) => {
      if (!deleted) return;
      invalidateDepartmentQueries(queryClient);
      toast({ title: t('DEPARTMENT_DELETED') });
    },
  });
};
